import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ComponentType, useMemo } from "react";
import { useLocation, Link } from "react-router-dom";
import { CellProps } from "react-table";

import { AdminLabOrderList, LabOrder } from "pwapi";

import {
  ExternalLabsState,
  ExternalLabStainsState,
} from "shared/reducers/frontLoaderReducers";
import { selectFeatureFlag } from "shared/reducers/sysReducer";
import { timestampToLocaleDateTimeString } from "shared/utils/dates";
import { splitFlagReason } from "shared/utils/labOrderUtils";
import { appendSendoutIfNeeded } from "shared/utils/labUtils";
import { getStainByLabStainId } from "shared/utils/stainUtils";

import { PathologyWatchButton } from "components/customInputs/buttons/Button";
import { useAppSelector } from "store/hooks";

import OrderCancellationButton from "./OrderCancellationButton";
import OrderCompletionButton from "./OrderCompletionButton";

const OrdersColumns =
  <P extends object>(
    Component: ComponentType<P>,
    options?: {
      hasFlagReason?: boolean;
      canOpenCase?: boolean;
      canCancel?: boolean;
    }
  ) =>
  (props: any) => {
    const location = useLocation();
    const labStains = useAppSelector(({ labStains }) => labStains);
    const stains = useAppSelector(({ stains }) => stains);

    const cancelSentOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelSentLabOrder")
    );
    const cancelAutomatedOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCancelAutomatedOrder")
    );
    const completeOrderFlag = useAppSelector((state) =>
      selectFeatureFlag(state, "canCompleteLabOrder")
    );

    const externalLabs = useAppSelector(
      ({ externalLabs }: { externalLabs: ExternalLabsState }) => externalLabs
    );
    const externalLabStains = useAppSelector(
      ({ externalLabStains }: { externalLabStains: ExternalLabStainsState }) =>
        externalLabStains
    );

    const columns = useMemo(
      () =>
        [
          ...(options?.canOpenCase
            ? [
                {
                  Header: "Case Accession",
                  accessor: "case__accession",
                  Cell: ({ row }: CellProps<AdminLabOrderList>) => (
                    <>{row.original.case_accession}</>
                  ),
                },
              ]
            : []),
          {
            Header: "Type",
            accessor: "type",
          },
          {
            Header: "Ordered Stains",
            accessor: "stain_name",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <>
                {row.original.lab_order_items
                  ?.map((item, idx) => {
                    const stain = getStainByLabStainId(
                      item.lab_stain_id || 0,
                      stains,
                      labStains
                    );
                    const labStain = labStains[item.lab_stain_id || 0];
                    const externalLab =
                      externalLabs[
                        externalLabStains[labStain?.external_lab_stain_id || 0]
                          ?.external_lab_id || 0
                      ];
                    const externalLabName = externalLab?.name
                      ? appendSendoutIfNeeded(externalLab.name)
                      : externalLab?.name;
                    return (
                      stain && (
                        <span key={idx}>
                          <span>{item.block_name}</span>{" "}
                          <strong>{stain.name}</strong>{" "}
                          <span style={{ color: "var(--steel)" }}>
                            {stain.category}
                          </span>
                          {externalLabName && <> ({externalLabName})</>}
                          {labStain?.barcode_mapping_stain_name && (
                            <>: {labStain.barcode_mapping_stain_name}</>
                          )}
                        </span>
                      )
                    );
                  })
                  .filter((i) => i)}
              </>
            ),
          },
          {
            Header: "Created At",
            accessor: "created_at",
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <>
                {timestampToLocaleDateTimeString(row.original.created_at || "")}
              </>
            ),
          },
          {
            Header: "Completed At",
            accessor: "completed_at",
            diableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) =>
              row.original.completed_at ? (
                <>
                  {timestampToLocaleDateTimeString(
                    row.original.completed_at || ""
                  )}
                </>
              ) : (
                <></>
              ),
          },
          {
            Header: "Status",
            accessor: "status",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              // TODO: Orders do not currently have a status, so there's nothing to change
              // <OrderStatusForm status={row.original.status} />
              <>
                {row.original.cancelled_at
                  ? "Cancelled"
                  : row.original.completed_at
                  ? "Complete"
                  : ""}
              </>
            ),
          },
          {
            Header: "Note",
            accessor: "note",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => {
              return (
                <div style={{ maxWidth: "400px" }}>
                  {options?.hasFlagReason && row.original.flag?.reason && (
                    <>
                      <FontAwesomeIcon icon="flag" color="var(--imperialRed)" />
                      <span style={{ color: "var(--barnRed)" }}>
                        {" "}
                        {splitFlagReason(row.original.flag.reason)}
                      </span>
                      <br />
                    </>
                  )}
                  {row.original.note}
                </div>
              );
            },
          },
          {
            Header: "Actions",
            disableSortBy: true,
            Cell: ({ row }: CellProps<AdminLabOrderList>) => (
              <div
                className="cell__content--horizontal cell__content--actions"
                style={{ gap: "0.5em" }}
              >
                {options?.canOpenCase && (
                  <Link
                    to={`/admin/cases/${row.original.case_id}`}
                    state={{
                      label: "Orders",
                      prevUrl: `${location.pathname}${location.search}`,
                    }}
                  >
                    <PathologyWatchButton size="small">
                      Edit Case
                    </PathologyWatchButton>
                  </Link>
                )}
                {completeOrderFlag &&
                  !row.original.completed_at &&
                  !row.original.cancelled_at && (
                    <OrderCompletionButton order={row.original as LabOrder} />
                  )}
                {options?.canCancel &&
                  ((row.original.user?.id && cancelSentOrderFlag) ||
                    (!row.original.user?.id && cancelAutomatedOrderFlag)) &&
                  !row.original.completed_at &&
                  !row.original.cancelled_at && (
                    <OrderCancellationButton order={row.original as LabOrder} />
                  )}
                {row.original.slide_id && (
                  <Link
                    to={`/cases/${row.original.case_id}/slides/${row.original.slide_id}`}
                    title="View Slide"
                    target="_blank"
                  >
                    <PathologyWatchButton size="small">
                      <>
                        <FontAwesomeIcon
                          icon="flag"
                          color="var(--imperialRed)"
                        />
                        &nbsp;&rarr;
                      </>
                    </PathologyWatchButton>
                  </Link>
                )}
              </div>
            ),
          },
        ] as any,
      []
    );

    return <Component columns={columns} {...props} />;
  };

export default OrdersColumns;
